// Crash and WebView-reload counter: the decision rules.
//
// Criterion #7 fails on a single counted event, so a false positive costs as
// much as a missed crash. Each event is logged with type, time and run label;
// `counted` says whether it counts against the criterion.
//
// Counted:
//   renderProcessGone            Android WebView render process lost (foreground)
//   webContentProcessTerminated  iOS WKWebView content process ended (foreground)
//   webViewReload                the page reloaded with no termination behind it
//   nativeCrash                  uncaught native exception
//   uncleanExit                  the process died while a labeled run recorded
// Logged, not counted:
//   runAborted                   app paused/backgrounded mid-run (flag cleared)
//   the two terminations above while in the background (system reclaim)
//   webViewReload caused by a termination already counted (one incident, one count)
//   activityRecreated            Android rebuilt the activity in a live process
//
// The run-in-progress flag is set only while a labeled run records. Cold-start
// trials never start one, so force-stopping between them counts nothing.
//
// The native halves (android Gate1Store.java, ios Gate1Plugin.swift) apply the
// same rules for the events they see first (terminations, crashes, pauses).

export const COUNTED_TYPES = [
    'renderProcessGone', 'webContentProcessTerminated', 'webViewReload', 'nativeCrash', 'uncleanExit',
]

export function emptyState() {
    return { events: [], runFlag: null, pendingReloadCause: null }
}

function event(type, counted, runLabel, now, detail = null, source = 'js') {
    return { type, counted, at: now, runLabel: runLabel ?? null, source, detail }
}

export function countsOf(events) {
    const counts = Object.fromEntries(COUNTED_TYPES.map(t => [t, 0]))
    let total = 0
    for (const e of events) {
        if (!e.counted) continue
        counts[e.type] = (counts[e.type] ?? 0) + 1
        total++
    }
    return { ...counts, total }
}

// What a JS boot means. A "bridge" is one native WebView host (Android:
// activity; iOS: view controller); a boot is one page load inside it.
//   bootsThisBridge > 1                 the page reloaded inside a live WebView
//   bootsThisBridge 1, bridges > 1      Android rebuilt the activity (after a pause)
//   bootsThisBridge 1, bridges 1        a fresh process: a launch
// Returns { events, clearRunFlag, clearPendingReloadCause }.
export function evaluateBoot({ state, bootsThisBridge, bridgesThisProcess, now }) {
    const label = state.runFlag?.label ?? null
    if (bootsThisBridge > 1) {
        if (state.pendingReloadCause) {
            return {
                events: [event('webViewReload', false, label, now, { reason: 'afterTermination', cause: state.pendingReloadCause })],
                clearRunFlag: !!state.runFlag,
                clearPendingReloadCause: true,
            }
        }
        return {
            events: [event('webViewReload', true, label, now, { bootsThisBridge })],
            clearRunFlag: !!state.runFlag,
            clearPendingReloadCause: false,
        }
    }
    if (bridgesThisProcess > 1) {
        return {
            events: [event('activityRecreated', false, label, now, { bridgesThisProcess })],
            clearRunFlag: !!state.runFlag,
            clearPendingReloadCause: !!state.pendingReloadCause,
        }
    }
    if (state.runFlag) {
        return {
            events: [event('uncleanExit', true, label, now, { runStartedAt: state.runFlag.startedAt ?? null })],
            clearRunFlag: true,
            clearPendingReloadCause: !!state.pendingReloadCause,
        }
    }
    // Cold start: nothing to count. A stale pending reload cause (the process
    // died before reloading) is dropped.
    return { events: [], clearRunFlag: false, clearPendingReloadCause: !!state.pendingReloadCause }
}

export function onRunBegin(state, { label, now }) {
    return { ...state, runFlag: { label, startedAt: now } }
}

export function onRunEnd(state) {
    return { ...state, runFlag: null }
}

// Paused/backgrounded mid-run: abort the run, clear the flag, count nothing.
export function onPause(state, { now }) {
    if (!state.runFlag) return { state, event: null }
    const ev = event('runAborted', false, state.runFlag.label, now, { reason: 'paused' })
    return { state: { ...state, runFlag: null, events: [...state.events, ev] }, event: ev }
}

// A WebView process loss. Counted only in the foreground. The run (if any)
// ends with this event, so the next launch does not also count an unclean exit.
export function onWebViewProcessLoss(state, { type, foreground, now, detail = {}, expectsReload }) {
    const ev = event(type, !!foreground, state.runFlag?.label, now, { ...detail, foreground: !!foreground })
    return {
        ...state,
        runFlag: null,
        pendingReloadCause: expectsReload ? { type, at: now } : state.pendingReloadCause,
        events: [...state.events, ev],
    }
}

export function onNativeCrash(state, { now, detail = {} }) {
    const ev = event('nativeCrash', true, state.runFlag?.label, now, detail)
    return { ...state, runFlag: null, events: [...state.events, ev] }
}

export function applyBoot(state, result) {
    return {
        ...state,
        events: [...state.events, ...result.events],
        runFlag: result.clearRunFlag ? null : state.runFlag,
        pendingReloadCause: result.clearPendingReloadCause ? null : state.pendingReloadCause,
    }
}
